import { Link } from 'expo-router';
import { useState } from 'react';
import { Pressable } from 'react-native';

import { Button } from '@/components/ui/Button';
import { Form } from '@/components/ui/Form';
import { PasswordField } from '@/components/ui/PasswordField';
import { Text } from '@/components/ui/Text';
import { TextField } from '@/components/ui/TextField';
import { PASSWORD_PLACEHOLDER } from '@/lib/constants';
import { supabase } from '@/lib/supabase';
import { useInFlightGuard } from '@/lib/useInFlightGuard';

export function SignInForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const runExclusive = useInFlightGuard();

  async function submit() {
    await runExclusive(async () => {
      setBusy(true);
      setError(null);
      try {
        const { error: authError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (authError) setError(authError.message);
      } catch {
        // A thrown transport error (vs a returned { error }) would otherwise leave
        // the button spinning forever; surface it and let `finally` clear busy.
        setError('Something went wrong. Please try again.');
      } finally {
        setBusy(false);
      }
    });
  }

  return (
    <Form onSubmit={submit} className="gap-3">
      <TextField
        label="Email"
        value={email}
        onChangeText={setEmail}
        placeholder="you@example.com"
        autoCapitalize="none"
        autoComplete="email"
        textContentType="username"
        importantForAutofill="yes"
        keyboardType="email-address"
        inputMode="email"
      />
      <PasswordField
        label="Password"
        autoComplete="current-password"
        textContentType="password"
        importantForAutofill="yes"
        labelRight={
          <Link href="/reset/request" asChild>
            <Pressable hitSlop={8}>
              <Text variant="caption" className="text-signal">
                Forgot?
              </Text>
            </Pressable>
          </Link>
        }
        value={password}
        onChangeText={setPassword}
        placeholder={PASSWORD_PLACEHOLDER}
      />

      {error ? (
        <Text variant="caption" className="text-alert">
          {error}
        </Text>
      ) : null}

      <Button block submit label="Sign in" busy={busy} />

      <Link href="/sign-up" asChild>
        <Pressable className="self-center">
          <Text variant="caption" className="text-muted">
            New here? Create account
          </Text>
        </Pressable>
      </Link>
    </Form>
  );
}
